import { useEffect, useRef, useState } from "react";
import { DiiaModal } from "./DiiaModal";
import { createDiiaSharing, getDiiaSharing, ShareDiiaResponse } from "@/src/api/server-actions/diia";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/src/shared/api";
import { useFormContext } from "react-hook-form";
import { formatDateTimeLight, normalizedDateFormat } from "@/src/shared/utils/parseDatePickerValue";
import { DiiaButton } from "@/src/components/DiiaSharing/DiiaButton";
import { getEngineTypeByLabel } from "@/src/shared/utils/formDataMiddleware";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import { safeParseJson } from "@/src/shared/utils/form";
import { isAndroid } from "react-device-detect";
import {addErrorLog} from "@/src/api/services/integration-log";
import {PROCESS_INSTANCE_QUERY} from "@/src/shared/const";
import {useSearchParams} from "next/navigation";

interface DiiaSharingPoolingProps {
  open: boolean;
  requestId: string | undefined;
  pollingInterval?: number;
  maxRequests?: number;
  handleOnSuccess: (data: any) => void;
}

function useDiiaSharingPooling({
  open,
  requestId,
  pollingInterval = 3000, // 3 секунд
  maxRequests = 60,
  handleOnSuccess,
}: DiiaSharingPoolingProps){
  const pollingCountRef = useRef<number>(0);
  const eventSourceRef = useRef<EventSource>();

  const { data: diiSharingDocument, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["diia-sharing", requestId],
    queryFn: () => getDiiaSharing(requestId!),
    enabled: !!requestId && open && pollingCountRef.current < maxRequests,
    refetchOnWindowFocus: true,
    refetchInterval: pollingInterval,
    refetchIntervalInBackground: true,
    select: safeParseJson
  })

  useEffect(() => {
    console.log('requestId', requestId);
    if (!requestId) return

    pollingCountRef.current = 0;
    const connectSSE = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      const eventSource = new EventSource(`${API_BASE_URL}/document-service/sse/${requestId}`);
      eventSourceRef.current = eventSource;
      eventSource.onopen = () => {
        console.log('SSE connection opened');
        refetch()
      }

      eventSource.onmessage = (event) => {
        const message = JSON.parse(event.data);
        console.log('SSE message:', message);
        handleOnSuccess(message);
      };

      eventSource.onerror = (e) => {
        eventSource.close();
        console.error('SSE error:', e);
      };
    };

    // Initial connection
    connectSSE();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page visible, reconnecting SSE');
        connectSSE();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      eventSourceRef.current?.close();
    };
  }, [requestId]);

  useEffect(() => {
    if (dataUpdatedAt !== undefined) {
      pollingCountRef.current += 1;
    }
    if (!diiSharingDocument || diiSharingDocument.sharedDocuments.length === 0) return
    handleOnSuccess(diiSharingDocument);
    eventSourceRef.current?.close();
  }, [diiSharingDocument, dataUpdatedAt])

  useEffect(() => {
    if (open === false) {
      eventSourceRef.current?.close();
    }
  }, [open]);

}

export const DiiaSharing = ({ label, scopes = ["INTERNAL_PASSPORT"],className }: { label: string, scopes: string[],className?:string }) => {
  const { setValue, getValues } = useFormContext()
  const order = useProcessInstanceStore((state) => state.processInstance.order);
  const updateField = useUpdateField()
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState<ShareDiiaResponse>();
  const [requestId, setRequestId] = useState<string>();

  const { mutate: addError } = useMutation({
    mutationFn: addErrorLog,
  });

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setMessage(undefined);
    setRequestId(undefined);
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createDiiaSharing,
    onMutate: () => {
      openModal();
    },
    onSuccess: (data) => {
      setMessage(data);
    },
    onError: (error) => {
        console.error("Error while creating diia sharing", error);
    }
  })

  const handleDiiaSharing = async () => {
    const data = await mutateAsync({ scopes, contextId: order.id });
    setRequestId(data.id);
  };


  const startSession = async () => {
    const data = await mutateAsync({ scopes, contextId: order.id });
    setRequestId(data.id);
  };

  const handleMobileRedirect = () => {
    if (message?.deeplink) {
      // open mobile app or installation page by deeplink
      const deeplink = `https://diia.page.link?link=${message?.deeplink}&apn=ua.gov.diia.app&isi=1489717872&ibi=ua.gov.diia.app`;
      if (isAndroid) {
          window.open(deeplink, "_blank");
      } else {
        const newWindow =  window.open(deeplink, "_blank")
        if (newWindow) {
          setTimeout(() => {
            newWindow.close();
          }, 500);
        }
      }
    }
  }

  const updateForm = (message: any) => {
    const passport = message.sharedDocuments.find((doc: { documentType: string; }) => doc.documentType === "INTERNAL_PASSPORT")
    if (passport) {
      const additionalParams = passport?.additionalParams
      const fields = [
        ["citizenship", { value: "UA", label: additionalParams["nationalityUA"] }],
        ["documentType", { value: "ID_CARD", label: "ID Паспорт громадянина України" }],
        ["identCode", additionalParams["taxpayerNumber"]],
        ["birthDate", formatDateTimeLight(additionalParams["birthday"], normalizedDateFormat, "DD.MM.YYYY")],
        ["lastName", additionalParams["lastNameUA"]],
        ["lastNameLatin", additionalParams["lastNameEN"]],
        ["firstName", additionalParams["firstNameUA"]],
        ["firstNameLatin", additionalParams["firstNameEN"]],
        ["middleName", additionalParams["middleNameUA"]],
        ["passportNumber", additionalParams["docNumber"]],
        ["issuingAuthority", additionalParams["department"]],
        ["issueDate", formatDateTimeLight(additionalParams["issueDate"], normalizedDateFormat, "DD.MM.YYYY")],
      ];
      fields.forEach(([field, value]) => {
        updateField(field, value)
        setValue(field, value, { shouldValidate: true })
      });
    }
    // vechicle
    const vechicle = message.sharedDocuments.find((doc: { documentType: string; }) => doc.documentType === "VEHICLE_LICENSE")
    if (vechicle) {
      const flowCode = getValues("flowCode")
      const additionalParams = vechicle?.additionalParams
      const baseFields = [
        ["plateNumber", additionalParams["ua.licensePlate.value"]],
        ["vin", additionalParams["ua.vin.value"]],
        ["brand", { value: additionalParams["ua.brand.value"], label: additionalParams["ua.brand.value"] }],
      ];
      const additionalFields = [
        ["model", { value: additionalParams["ua.model.value"], label: additionalParams["ua.model.value"] }],
        ["constructionYear", additionalParams["ua.makeYear.value"]],
        ["engineType", getEngineTypeByLabel(additionalParams["ua.fuel.value"])],
        ["totalWeight", additionalParams["ua.totalWeight.value"]],
        ["ownWeight", additionalParams["ua.ownWeight.value"]],
        ["placesCount", additionalParams["ua.nseating.value"]],
        ["engineCapacity", additionalParams["ua.capacity.value"]],
      ];

      [
        ...baseFields,
        ...(flowCode === "GreenCard_01.01.2025" ? [] : additionalFields),
      ].forEach(([field, value]) => {
        updateField(field, value)
        setValue(field, value, { shouldValidate: true })
      });
    }
    const foreignPassport = message.sharedDocuments.find((doc: { documentType: string; }) => doc.documentType === "FOREIGN_PASSPORT")
    if (foreignPassport) {
      const additionalParams = foreignPassport?.additionalParams
      const fields = [
        ["citizenship", { value: additionalParams["countryCode"] === "UA" ? "UA" : additionalParams["countryCode"], label: additionalParams["nationalityUA"] }],
        ["documentType", { value: "TRAVELLING_PASSPORT", label: "Закордонний паспорт" }],
        ["identCode", additionalParams["taxpayerNumber"]],
        ["birthDate", formatDateTimeLight(additionalParams["birthday"], normalizedDateFormat, "DD.MM.YYYY")],
        ["lastName", additionalParams["lastNameUA"]],
        ["lastNameLatin", additionalParams["lastNameEN"]],
        ["firstName", additionalParams["firstNameUA"]],
        ["firstNameLatin", additionalParams["firstNameEN"]],
        ["middleName", additionalParams["middleNameUA"]],
        ["foreignPassportNumber", additionalParams["docNumber"]],
        ["passportSeries", additionalParams["docNumber"].substring(0, 2)],
        ["passportNumber", additionalParams["docNumber"].substring(2)],
        ["issuingAuthority", additionalParams["departmentEN"]],
        ["issueDate", formatDateTimeLight(additionalParams["issueDate"], normalizedDateFormat, "DD.MM.YYYY")],
      ];
      fields.forEach(([field, value]) => {
        updateField(field, value)
        setValue(field, value, { shouldValidate: true })
      });
    }
    const driverLicense = message.sharedDocuments.find((doc: { documentType: string; }) => doc.documentType === "DRIVER_LICENSE")
    if (driverLicense) {
      const additionalParams = driverLicense?.additionalParams
      const birthDate = additionalParams["ua.birth.value"].split("\n")[0]
      const [firstName, middleName] = additionalParams["ua.firstName.value"].split(" ")
      const fields = [
        ["citizenship", { value: "UA", label: "Україна" }],
        ["birthDate", formatDateTimeLight(birthDate, normalizedDateFormat, "DD.MM.YYYY")],
        ["lastName", additionalParams["ua.lastName.value"]],
        ["lastNameLatin", additionalParams["eng.lastName.value"]],
        ["firstName", firstName],
        ["firstNameLatin", additionalParams["eng.firstName.value"]],
        ["middleName", middleName],
        ["passportNumber", additionalParams["ua.documentNumber.value"]],
        ["issuingAuthority", additionalParams["ua.department.value"]],
        ["issueDate", formatDateTimeLight(additionalParams["ua.issueDate.value"], normalizedDateFormat, "DD.MM.YYYY")]
      ];
      fields.forEach(([field, value]) => {
        updateField(field, value)
        setValue(field, value, { shouldValidate: true })
      });
    }
  }

  useDiiaSharingPooling({
    requestId,
    open: isModalOpen,
    handleOnSuccess: (data: any) => {
      updateForm(data)
      addError({
        actionName: "frontendDiiaSharing",
        processInstanceId: processInstanceId!,
        message: JSON.stringify(data),
        statusCode: 200,
        requestMethod: "GET",
      })
      closeModal()
    }
  })

  return (
    <>
      <DiiaButton onClick={startSession} label={label} className={className}/>
      <DiiaModal loading={isPending} url={message?.deeplink} handleDiiaSharing={handleDiiaSharing} open={isModalOpen}
        handleClose={closeModal} diiaButtonProps={{label, onClick: handleMobileRedirect}} scopes={scopes} />
    </>
  );
};
